import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../Translations/LenguageContext";
import RotatingLogo from "../../../Loaders/RotatingLogo";
import { AiOutlineLike, AiOutlineDislike } from "react-icons/ai";
import { BsInfo } from "react-icons/bs";
import { LuTrash2 } from "react-icons/lu";
import { PiPlus } from "react-icons/pi";
import PromptsLoader from "../../../Loaders/PromptsLoader";
import NewPromptModal from "./NewPromptsModal";
import DeleteItemModal from "../../../Components/DeleteItemModal";
import UpdatePromptRLStatusModal from "./UpdatePromptRLStatusModal";
import GeoPromptInfoModal from "./GeoPromptInfoModal";
import Spinner from "../../../Components/Spinner";
import ErrorMessage from "../../../Components/ErrorMessage";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const GeoPrompts = ({
    account_id,
    org_id,
    geo_id,
    promptsGenStatus,
    setPromptsGenStatus,
    runPromptSimulations,
    setLoadingSave,
    loadingSave,
    errorMessage,
    setErrorMessage
}) => {
    const { t } = useLanguage()
    const navigate = useNavigate()
    const [loadingPrompts, setLoadingPrompts] = useState(false)
    const [prompts, setPrompts] = useState([])
    const [openNewPrompt, setOpenNewPrompt] = useState(false)
    const [openUpdatePrompt, setOpenUpdatePrompt] = useState(false)
    const [selectedPrompt, setSelectedPrompt] = useState({})
    const [openDeletePromptModal, setOpenDeletePromptModal] = useState(false)
    const [selectedStatusRL, setSelectedStatusRL] = useState(null)
    const [openInfoModal, setOpenInfoModal] = useState(false)
    const [jobsStatus, setJobsStatus] = useState({})
    const [simulationCount, setSimulationCount] = useState(0)
    const [statusGenPrompts, setStatusGenPrompts] = useState(false)
    const [failedPrompts, setFailedPrompts] = useState(false);

    const navigateBack = () => {
        setLoadingSave(false)
        setErrorMessage({ message: "", display: false });
        navigate(`/console/organizations/${org_id}/geo/${geo_id}/engines`);
    };

    const pollStatus = async () => {
        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_geo_prompts_gen_status`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            if (result.data.prompts_gen_status == "PENDING") {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/engines`)
            }

            if (result.data.prompts_gen_status) {
                setPromptsGenStatus(result.data.prompts_gen_status)
            }

            if (result.data.simulation_count) {
                setSimulationCount(Number(result.data.simulation_count))
            }

            if (result.data.prompts_gen_queue_status) {
                const queueStatus = result.data.prompts_gen_queue_status;

                setJobsStatus(queueStatus);

                const hasFailedJobs = Object.values(queueStatus).some(
                    status => status === "FAILED"
                );

                setErrorMessage({ message: "Some prompts failed to generate.... please go back and restart the generation", display: true })

                setFailedPrompts(hasFailedJobs);
            }

        } catch (err) {
            console.error("Error fetching GEO optimization:", err);
        }
    };

    const getPrompts = async () => {
        setLoadingPrompts(true);

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_geo_prompts`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setPrompts(result.prompts)

        } catch (err) {
            console.error("Error fetching GEO optimization:", err);
        } finally {
            setTimeout(() => {
                setLoadingPrompts(false);
            }, 500);
        }
    };

    const pollPrompts = async () => {

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_geo_prompts`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setPrompts(result.prompts)
            if (result.prompts.length >= simulationCount) {
                setStatusGenPrompts(false)
            }

        } catch (err) {
            console.error("Error fetching GEO optimization:", err);
        }
    };

    const handleRlPrompt = (prompt, status) => {
        setSelectedPrompt(prompt)
        setSelectedStatusRL(status)
        setOpenUpdatePrompt(true)
    }

    const handleOpenInfoModal = (prompt) => {
        setSelectedPrompt(prompt)
        setOpenInfoModal(true)
    }

    const handleDeletePrompt = (prompt) => {
        setSelectedPrompt(prompt)
        setOpenDeletePromptModal(true)
    }

    const handleOpenNewPromptModal = () => {
        setOpenNewPrompt(true)
    }

    useEffect(() => {
        pollStatus();
        getPrompts()
    }, [promptsGenStatus, account_id, org_id, geo_id]);

    useEffect(() => {
        const shouldPoll = ["QUEUED", "RUNNING"].includes(promptsGenStatus);

        if (!shouldPoll) return;

        const interval = setInterval(() => {
            pollStatus();
        }, 5000);

        return () => clearInterval(interval);
    }, [promptsGenStatus, account_id, org_id, geo_id]);

    useEffect(() => {
        const allJobsCompleted =
            Object.keys(jobsStatus ?? {}).length > 0 &&
            Object.values(jobsStatus).every(
                status => status === "COMPLETED"
            );

        const allPromptsGenerated =
            prompts.length >= Number(simulationCount);

        const shouldContinuePolling =
            !(allJobsCompleted && allPromptsGenerated);

        setStatusGenPrompts(shouldContinuePolling);

        if (!shouldContinuePolling) return;

        const interval = setInterval(() => {
            pollPrompts();
        }, 5000);

        return () => clearInterval(interval);
    }, [
        jobsStatus,
        prompts.length,
        simulationCount,
        account_id,
        org_id,
        geo_id
    ]);


    if (!promptsGenStatus) {
        return (
            <div className="geo_context_cont fade-in">
                <RotatingLogo />
            </div>
        )
    }

    if (promptsGenStatus == "COMPLETED") {
        return (
            <div className="geo_context_cont">
                <p className="add_new_source_cont_title fade-in">
                    {t.geo.simulated_prompts}
                </p>

                <p className="add_new_source_cont_desc fade-in">
                    {t.geo.simulated_prompts_desc}
                </p>

                {loadingPrompts ? (
                    <PromptsLoader />
                ) : (
                    <>
                        <div className="prompts_main_container">
                            <div className="prompts_main_container_header">
                                <p className="prompts_main_container_title fade-in">
                                    <span className={statusGenPrompts ? "prompts_main_container_title_beam" : ""}>
                                        {prompts.length} / {simulationCount} {t.geo.simulated_prompts}
                                    </span>
                                </p>
                                {!statusGenPrompts && (
                                    <button
                                        type="button"
                                        className="source_website_pages_list_header_btn fade-in"
                                        onClick={handleOpenNewPromptModal}
                                    >
                                        <PiPlus />{" "}
                                        {t.geo.add_custom_prompt}
                                    </button>
                                )}
                            </div>
                            <div className="prompts_cont fade-in">
                                {prompts.length == 0 ? (
                                    <div className="gen_engine_animation_cont_running">
                                        <div className="geo_engine_cont_running">
                                            <span className="geo_engine_item_running fade-in" />
                                            <span className="geo_engine_item_running fade-in" />
                                            <span className="geo_engine_item_running fade-in" />
                                            <span className="geo_engine_item_running fade-in" />
                                            <span className="geo_engine_item_running fade-in" />
                                        </div>
                                    </div>
                                ) : (
                                    prompts.map((prompt) => (
                                        <div key={prompt.id} className={`prompts_item fade-in ${prompt.id.startsWith("prompt-0-0-") && "prompts_item_custom"}`}>
                                            <p>"{prompt.prompt}"</p>
                                            <div className="prompts_item_footer">
                                                <div className="prompts_item_footer_btn_cont">
                                                    <button
                                                        className={`prompts_item_btn ${prompt.rl_status == "POSITIVE" && "prompts_item_btn_selected"}`}
                                                        onClick={() => handleRlPrompt(prompt, "POSITIVE")}
                                                    >
                                                        <AiOutlineLike />
                                                    </button>
                                                    <button
                                                        className={`prompts_item_btn ${prompt.rl_status == "NEGATIVE" && "prompts_item_btn_selected"}`}
                                                        onClick={() => handleRlPrompt(prompt, "NEGATIVE")}
                                                    >
                                                        <AiOutlineDislike />
                                                    </button>
                                                    <button
                                                        className="prompts_item_btn"
                                                        onClick={() => handleOpenInfoModal(prompt)}
                                                    >
                                                        <BsInfo />
                                                    </button>
                                                </div>
                                                <div className="prompts_item_footer_btn_cont">
                                                    <button
                                                        className="prompts_item_btn"
                                                        onClick={() => handleDeletePrompt(prompt)}
                                                    >
                                                        <LuTrash2 />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                        <p className="warning_source_message fade-in">
                            {t.geo.gen_prompt_warning}
                        </p>

                        {failedPrompts && (
                            <>
                                <ErrorMessage err={errorMessage} />
                                <div className="open_frame_body_footer">
                                    <button
                                        type="button"
                                        className="modale_save_btn fade-in"
                                        onClick={navigateBack}
                                    >
                                        <i className="fa-solid fa-chevron-left"></i>
                                        {t.back}
                                    </button>
                                </div>
                            </>
                        )}
                        {(promptsGenStatus == "COMPLETED" && !statusGenPrompts && !failedPrompts) && (
                            <div className="open_frame_body_footer">
                                <button
                                    type="button"
                                    className="modale_save_btn fade-in"
                                    onClick={navigateBack}
                                >
                                    <i className="fa-solid fa-chevron-left"></i>
                                    {t.back}
                                </button>

                                <button
                                    type="button"
                                    className="modale_save_btn fade-in"
                                    onClick={runPromptSimulations}
                                >
                                    {loadingSave ? (
                                        <Spinner loading={loadingSave} />
                                    ) : (
                                        <>
                                            {t.next}
                                            <i className="fa-solid fa-chevron-right"></i>
                                        </>
                                    )}
                                </button>
                            </div>
                        )}

                        {(statusGenPrompts && !failedPrompts) && (
                            <Spinner loading={true} />
                        )}

                    </>
                )}

                <NewPromptModal
                    account_id={account_id}
                    org_id={org_id}
                    geo_id={geo_id}
                    show={openNewPrompt}
                    handleClose={() => setOpenNewPrompt(false)}
                    prompts={prompts}
                    getPrompts={getPrompts}
                />

                <UpdatePromptRLStatusModal
                    account_id={account_id}
                    org_id={org_id}
                    geo_id={geo_id}
                    show={openUpdatePrompt}
                    handleClose={() => setOpenUpdatePrompt(false)}
                    selectedPrompt={selectedPrompt}
                    selectedStatusRL={selectedStatusRL}
                    getPrompts={getPrompts}
                />

                <GeoPromptInfoModal
                    show={openInfoModal}
                    handleClose={() => setOpenInfoModal(false)}
                    selectedPrompt={selectedPrompt}
                />

                <DeleteItemModal
                    account_id={account_id}
                    show={openDeletePromptModal}
                    handleClose={() => setOpenDeletePromptModal(false)}
                    fetchRecords={getPrompts}
                    item={selectedPrompt}
                    endpoint={"delete_prompt"}
                    title={t.geo.delete_prompt}
                    description={t.geo.delete_prompt_desc}
                    input_value={selectedPrompt}
                />
            </div >
        )
    }

    if (promptsGenStatus == "QUEUED") {
        return (
            <div className="gen_topic_animation_container fade-in">
                <div className="gen_topic_animation_cont">
                    <div className="gen_topic_cont_queued">
                        <p className="add_new_source_cont_title fade-in">
                            {t.geo.preparing_prompt_simulations}
                        </p>

                        <p className="add_new_source_cont_desc fade-in">
                            {t.geo.preparing_prompt_simulations_desc}
                        </p>
                        <div className="nested-circle-loader fade-in">
                            <div className="orbit orbit-1">
                            </div>
                            <div className="orbit orbit-2">
                            </div>
                            <div className="orbit orbit-3">
                            </div>
                            <div className="orbit orbit-4">
                            </div>
                            <div className="orbit-center" />
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

export default GeoPrompts